import { useState, useCallback, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X } from 'lucide-react';
import { toast } from 'sonner';
import type { PhotoListItem } from '@/lib/api';
import { useReorderAlbumPhotos, useRemovePhotoFromAlbum } from '@/hooks/useAlbums';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface SortableAlbumPhotosProps {
  albumId: string;
  photos: PhotoListItem[];
  onPhotoClick?: (photo: PhotoListItem) => void;
}

export function SortableAlbumPhotos({ albumId, photos, onPhotoClick }: SortableAlbumPhotosProps) {
  const [items, setItems] = useState(photos);
  const [isReordering, setIsReordering] = useState(false);
  const reorderPhotos = useReorderAlbumPhotos(albumId);
  const removePhoto = useRemovePhotoFromAlbum(albumId);

  // Update items when photos prop changes
  useEffect(() => {
    setItems(photos);
  }, [photos]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      // Explicitly save old items before mutation for rollback
      const oldItems = [...items];
      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);

      // Save new order to server
      try {
        await reorderPhotos.mutateAsync({
          photoIds: newItems.map((item) => item.id),
        });
        toast.success('写真の順序を変更しました');
      } catch (error) {
        // Revert to explicitly saved old items
        setItems(oldItems);
        const errorMessage =
          error instanceof Error ? error.message : '写真の並び替えに失敗しました';
        toast.error(errorMessage);
      }
    },
    [items, reorderPhotos]
  );

  const handleRemovePhoto = async (photoId: string) => {
    try {
      await removePhoto.mutateAsync(photoId);
      toast.success('写真をアルバムから削除しました');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '写真の削除に失敗しました';
      toast.error(errorMessage);
    }
  };

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-muted p-4">
          <svg
            className="h-12 w-12 text-muted-foreground"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 className="mt-4 text-lg font-medium">このアルバムには写真がありません</h3>
        <p className="mt-1 text-sm text-muted-foreground">写真を追加してください。</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {isReordering
            ? 'ドラッグ&ドロップで写真の順序を変更できます。'
            : `${items.length}枚の写真`}
        </p>
        <Button variant="outline" size="sm" onClick={() => setIsReordering(!isReordering)}>
          {isReordering ? '完了' : '並び替え'}
        </Button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((p) => p.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {items.map((photo) => (
              <SortablePhotoItem
                key={photo.id}
                photo={photo}
                isReordering={isReordering}
                onClick={() => !isReordering && onPhotoClick?.(photo)}
                onRemove={() => handleRemovePhoto(photo.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

interface SortablePhotoItemProps {
  photo: PhotoListItem;
  isReordering: boolean;
  onClick?: () => void;
  onRemove: () => void;
}

function SortablePhotoItem({ photo, isReordering, onClick, onRemove }: SortablePhotoItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: photo.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative aspect-square overflow-hidden rounded-lg border bg-card transition-shadow',
        isDragging && 'shadow-xl',
        !isReordering && 'cursor-pointer hover:shadow-md'
      )}
      onClick={!isReordering ? onClick : undefined}
    >
      <img
        src={photo.thumbnailUrl}
        alt={photo.originalFilename || 'Photo'}
        className="h-full w-full object-cover"
        loading="lazy"
        draggable={false}
      />

      {isReordering && (
        <div
          {...attributes}
          {...listeners}
          className="absolute left-2 top-2 cursor-grab rounded bg-black/60 p-1.5 text-white active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" />
        </div>
      )}

      {/* Remove button - visible on hover always, always visible in reorder mode */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className={cn(
          'absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white transition-colors hover:bg-destructive',
          isReordering ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        )}
        aria-label="アルバムから削除"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Photo info overlay */}
      {!isReordering && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
          {photo.worldName && <p className="text-xs text-white truncate">{photo.worldName}</p>}
        </div>
      )}
    </div>
  );
}
